import type { WidgetConfig } from '@digital-cs/shared';

/**
 * 从 OSS 视频 URL 派生首帧海报 URL(视频截帧)。
 *
 * 背景:配置接口 digitalHumanAssets[i].posterUrl 需要后端单独生成并回填,
 * 目前常返回 null;但对象本身允许匿名读,直接对视频 URL 应用 OSS 处理参数
 * video/snapshot 即可拿到首帧 jpg。
 *
 * 注意必须剥掉原签名参数(OSSAccessKeyId/Expires/Signature):x-oss-process
 * 参与签名校验,挂在已签名 URL 上会 SignatureDoesNotMatch(403);
 * 匿名 URL + 处理参数实测 prod/dev 桶均 200。
 *
 * 仅对自家 OSS 域名启用;非 OSS 来源返回 null(调用方走空态)。
 */
const OSS_HOST_RE = /^oss-.*\.iwanshang\.cloud$/i;

export function deriveOssSnapshotUrl(videoUrl: string | null | undefined): string | null {
  if (!videoUrl) return null;
  let u: URL;
  try {
    u = new URL(videoUrl);
  } catch {
    return null;
  }
  if (!OSS_HOST_RE.test(u.hostname)) return null;
  // w_480 缩到 480px 宽(高度按比例):首屏海报仅作加载期占位,原尺寸截帧动辄数百 KB,
  // 慢网会从上到下渐进刷屏;缩参后约几 KB 秒开,被视频顶替前肉眼无感。
  return `${u.origin}${u.pathname}?x-oss-process=video/snapshot,t_0,f_jpg,w_480`;
}

/**
 * 数字人加载期海报 URL:idle 素材(缺省回退首个素材)的后端 posterUrl,
 * 未回填(当前常态)时前端从 OSS 视频 URL 派生截帧(t_0 = 视频首帧)。
 *
 * DigitalHumanVideo 渲染海报 与 poster-preload 启动预载 必须共用此函数:
 * 两路 URL 逐字节一致浏览器缓存才命中,预载才有效。
 */
export function resolveDigitalPosterUrl(
  cfg: Pick<WidgetConfig, 'digitalHumanAssets'> | null | undefined,
): string | null {
  const asset =
    cfg?.digitalHumanAssets?.find((a) => a.state === 'idle') ?? cfg?.digitalHumanAssets?.[0];
  if (!asset) return null;
  return asset.posterUrl ?? deriveOssSnapshotUrl(asset.videoUrl);
}
